import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useT } from '../lib/i18n'
import {
  EMPTY_ONBOARDING_ANSWERS,
  ONBOARDING_STEPS,
  type OnboardingAnswers,
  type OnboardingIntent,
  type OnboardingRole,
  type OnboardingStep,
} from '../../shared/onboarding'
import { INTENT_OPTIONS, ROLE_OPTIONS, type OnboardingOption } from '../lib/onboardingOptions'
import { Modal, ModalLede, ModalTitle } from './ui/modal'
import { Button } from './ui/button'
import { Pressable } from './ui/pressable'
import { Note } from './ui/note'
import { Wordmark } from './ui/wordmark'
import {
  AppWindowIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CodeIcon,
  CompassIcon,
  HelpIcon,
  ImageIcon,
  LayoutTemplateIcon,
  MailIcon,
  MegaphoneIcon,
  PrototypeIcon,
  RocketIcon,
  SparkIcon,
  VectorIcon,
} from './ui/icons'

/**
 * The onboarding questionnaire: two questions, one screen each, both
 * skippable. Home shows it once to an account with no answers; Settings
 * reopens it with the current ones. `onDone` receives the answers (a skipped
 * question is null) and saves them; a rejection keeps the modal open with the
 * error under the actions. Closing the modal (Esc, the backdrop) hands over
 * what is answered so far, so the person is not asked again.
 */

const glyph = { size: 17 } as const

const ROLE_GLYPHS: Record<OnboardingRole, ReactNode> = {
  designer: <VectorIcon {...glyph} />,
  engineer: <CodeIcon {...glyph} />,
  vibe_coder: <SparkIcon {...glyph} />,
  founder: <RocketIcon {...glyph} />,
  marketer: <MegaphoneIcon {...glyph} />,
  exploring: <CompassIcon {...glyph} />,
}

const INTENT_GLYPHS: Record<OnboardingIntent, ReactNode> = {
  landing_page: <LayoutTemplateIcon {...glyph} />,
  product_ui: <AppWindowIcon {...glyph} />,
  prototype: <PrototypeIcon {...glyph} />,
  social: <ImageIcon {...glyph} />,
  email: <MailIcon {...glyph} />,
  unsure: <HelpIcon {...glyph} />,
}

const TOTAL = ONBOARDING_STEPS.length

export function OnboardingSurvey({
  initial = EMPTY_ONBOARDING_ANSWERS,
  onDone,
}: {
  initial?: OnboardingAnswers
  onDone: (answers: OnboardingAnswers) => Promise<void>
}) {
  const t = useT()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<OnboardingAnswers>(initial)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const current: OnboardingStep = ONBOARDING_STEPS[step] ?? 'role'
  const last = step === TOTAL - 1

  async function finish(final: OnboardingAnswers) {
    if (saving) return
    setSaving(true)
    setFailed(false)
    try {
      await onDone(final)
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  /** forward with the step's answer kept, or with it cleared when skipping */
  function advance(next: OnboardingAnswers) {
    setAnswers(next)
    if (last) void finish(next)
    else setStep(step + 1)
  }

  function skip() {
    advance(current === 'role' ? { ...answers, role: null } : { ...answers, intent: null })
  }

  const answered = current === 'role' ? answers.role !== null : answers.intent !== null
  /* the canvas listens for shortcuts on the window (Space pans, letters pick
     tools), and a focused option is a button, which those checks do not treat
     as typing: keys stay inside the modal, except Escape, which Radix closes
     it on before anything else sees it */
  const keepKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') e.stopPropagation()
  }
  const stepLabel = t('onboarding.stepOf', { step: step + 1, total: TOTAL })

  return (
    <Modal
      size="lg"
      showClose={false}
      className="flex flex-col p-0 sm:p-0"
      onClose={() => void finish(answers)}
      onKeyDown={keepKeys}
      onKeyUp={keepKeys}
      data-testid="onboarding-survey"
    >
      <div className="flex items-center justify-between px-5 pt-5 sm:px-8 sm:pt-7">
        <Wordmark size="sm" />
        <div className="flex items-center gap-1.5" aria-label={stepLabel}>
          {ONBOARDING_STEPS.map((id, i) => (
            <span
              key={id}
              aria-hidden
              className={cn('block h-1 w-7 rounded-full', i <= step ? 'bg-ink' : 'bg-line-soft')}
            />
          ))}
          <span className="ml-1.5 font-mono text-caption text-ink-faint">{stepLabel}</span>
        </div>
      </div>

      <div className="px-5 pt-6 sm:px-8">
        {current === 'role' ? (
          <Question title={t('onboarding.roleTitle')} lede={t('onboarding.roleLede')}>
            {ROLE_OPTIONS.map((option) => (
              <OptionTile
                key={option.id}
                option={option}
                glyph={ROLE_GLYPHS[option.id]}
                selected={answers.role === option.id}
                onClick={() => setAnswers({ ...answers, role: option.id })}
              />
            ))}
          </Question>
        ) : (
          <Question title={t('onboarding.intentTitle')} lede={t('onboarding.intentLede')}>
            {INTENT_OPTIONS.map((option) => (
              <OptionTile
                key={option.id}
                option={option}
                glyph={INTENT_GLYPHS[option.id]}
                selected={answers.intent === option.id}
                onClick={() => setAnswers({ ...answers, intent: option.id })}
              />
            ))}
          </Question>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 px-5 py-5 sm:px-8 sm:pb-7 sm:pt-7">
        {step === 0 ? (
          <span className="text-body text-ink-faint max-sm:hidden">{t('onboarding.hint')}</span>
        ) : (
          <Button variant="bare" size="sm" className="-ml-2" disabled={saving} onClick={() => setStep(step - 1)}>
            <ArrowLeftIcon size="rail" />
            {t('onboarding.backButton')}
          </Button>
        )}
        <span className="flex-1" />
        {failed && <Note tone="error">{t('onboarding.saveFailed')}</Note>}
        <Button variant="bare" disabled={saving} onClick={skip}>
          {t('onboarding.skipButton')}
        </Button>
        <Button variant="primary" disabled={!answered || saving} onClick={() => advance(answers)}>
          {last ? t('onboarding.finishButton') : t('onboarding.continueButton')}
          <ArrowRightIcon size="rail" />
        </Button>
      </div>
    </Modal>
  )
}

function Question({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  return (
    <>
      <ModalTitle className="text-title font-semibold">{title}</ModalTitle>
      <ModalLede className="mt-1.5 text-lead">{lede}</ModalLede>
      <div className="mt-6 grid gap-2.5 sm:grid-cols-2" role="radiogroup">
        {children}
      </div>
    </>
  )
}

/** One option: a glyph in a tile, a label, a blurb and a pick disc in the corner. */
function OptionTile<Id extends string>({
  option,
  glyph,
  selected,
  onClick,
}: {
  option: OnboardingOption<Id>
  glyph: ReactNode
  selected: boolean
  onClick: () => void
}) {
  const t = useT()
  return (
    <Pressable
      role="radio"
      aria-checked={selected}
      data-option={option.id}
      focus="ring"
      onClick={onClick}
      className={cn(
        'relative flex items-start gap-3 rounded-card py-3.5 pl-3 pr-9 transition-shadow',
        selected
          ? 'bg-brand/[0.06] shadow-[0_0_0_1.5px_var(--brand)]'
          : 'bg-surface shadow-ring hover:shadow-ring-hover',
      )}
    >
      <span
        className={cn(
          'grid size-8 flex-none place-items-center rounded-control',
          selected ? 'bg-brand/[0.12] text-brand' : 'bg-sub text-ink',
        )}
      >
        {glyph}
      </span>
      <span className="block min-w-0">
        <span className="block text-copy font-medium">{t(option.label)}</span>
        <span className="mt-0.5 block text-body text-ink-soft">{t(option.blurb)}</span>
      </span>
      <span
        aria-hidden
        className={cn(
          'absolute right-3 top-3 grid size-4 place-items-center rounded-full text-paper',
          selected ? 'bg-brand' : 'hidden',
        )}
      >
        {selected && <CheckIcon size={10} strokeWidth={2.4} />}
      </span>
    </Pressable>
  )
}
